// src/pages/screen1.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import {
  apiClient,
  getActiveBatchTempByMachine,
  getProdByMachineAndRef,
  getRtOperationsCount,
} from "../api";
import DoughnutChart2 from "../components/chart9";
import RTBarChartWithStdLine from "../components/chart10";

const Screen1 = ({ id_c: idCProp }) => {
  const { t } = useContext(LanguageContext);
  const navigate = useNavigate();

  const company = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("company")) || {};
    } catch {
      return {};
    }
  }, []);

  const id_c =
    idCProp ??
    (() => {
      try {
        return JSON.parse(localStorage.getItem("company"))?.id_c ?? null;
      } catch {
        return null;
      }
    })();

  // =========================
  // 0) REFRESH TICK (cada 10s) — SOLO Screen1
  // =========================
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setRefreshTick((v) => v + 1), 30000);
    return () => clearInterval(iv);
  }, []);

  // =========================
  // 1) Fecha y hora en vivo (solo UI)
  // =========================
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  const fmtDateDMY = (d) => {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const fmtDateYMD = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const fmtTimeHMS = (d) => {
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${hh}:${mi}:${ss}`;
  };

  const fmtTimeHH00 = (d) => {
    const hh = String(d.getHours()).padStart(2, "0");
    return `${hh}:00`;
  };

  // ✅ normaliza fechas ISO tipo 2026-02-22T05:00:00.000Z → 2026-02-22
  const toYMD = (v) => {
    if (!v) return "";
    const s = String(v);
    if (s.includes("T")) return s.slice(0, 10);
    if (v instanceof Date) return fmtDateYMD(v);
    return s.slice(0, 10);
  };

  const ddmmyyyy = useMemo(() => fmtDateDMY(now), [now]);
  const hhmmss = useMemo(() => fmtTimeHMS(now), [now]);

  // =========================
  // 2) Selector de UNA máquina
  // =========================
  const [allMachines, setAllMachines] = useState([]);
  const [machineCode, setMachineCode] = useState("");

  // =========================
  // 3) Lote activo (batches_temp) por máquina
  // =========================
  const [activeBatch, setActiveBatch] = useState(null);
  const [activeBatchStatus, setActiveBatchStatus] = useState({
    loading: false,
    reason: null,
    message: null,
    count: 0,
  });

  // =========================
  // 3b) Info del producto (prod_list)
  // =========================
  const [productInfo, setProductInfo] = useState(null);
  const [productStatus, setProductStatus] = useState({
    loading: false,
    reason: null,
    message: null,
  });

  // =========================
  // 4) Operaciones RT (total) desde inicio de lote hasta "ahora"
  // =========================
  const [rtOps, setRtOps] = useState(0);

  // =========================
  // 4c) Operaciones por hora (para barras)
  // =========================
  const [rtOpsByHour, setRtOpsByHour] = useState([]);

  // =========================
  // 4b) Horas utilizadas (en vivo) y Ops reales/hr (derivados)
  // =========================
  const hoursUsed = useMemo(() => {
    if (
      !activeBatch ||
      activeBatchStatus.reason === "no_active_batch" ||
      activeBatchStatus.reason === "multiple_active_batches"
    ) {
      return 0;
    }

    const startDateYMD = toYMD(activeBatch.date_begin);
    const startTime = String(activeBatch.time_begin || "").slice(0, 8);
    if (!startDateYMD || !startTime) return 0;

    let start = new Date(`${startDateYMD}T${startTime}`);
    const end = now;

    let diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) {
      const startMinus1Day = new Date(start);
      startMinus1Day.setDate(startMinus1Day.getDate() - 1);
      start = startMinus1Day;
      diffMs = end.getTime() - startMinus1Day.getTime();
    }

    const hours = diffMs / (1000 * 60 * 60);
    return Number.isFinite(hours) && hours > 0 ? Number(hours.toFixed(2)) : 0;
  }, [activeBatch, activeBatchStatus.reason, now]);

  const realOpsPerHour = useMemo(() => {
    if (!activeBatch || hoursUsed <= 0) return 0;
    const v = Number(rtOps || 0) / Number(hoursUsed || 1);
    return Number.isFinite(v) ? Number(v.toFixed(2)) : 0;
  }, [activeBatch, rtOps, hoursUsed]);

  // =========================
  // ✅ Q PRODUCTOS ESTIMADOS
  // Q Prod Estim = (Q PRODUCTOS ESTANDAR / HORA / OPERACIONES ESTANDAR / HORA) * OPERACIONES
  // =========================
  const estimatedProducts = useMemo(() => {
    if (
      !activeBatch ||
      activeBatchStatus.reason === "no_active_batch" ||
      activeBatchStatus.reason === "multiple_active_batches"
    ) {
      return null;
    }

    const qStdHr = Number(productInfo?.q_prod_std_hr);
    const opsStdHr = Number(productInfo?.ciclo_x_hr_prod_std);
    const ops = Number(rtOps || 0);

    if (!Number.isFinite(qStdHr) || !Number.isFinite(opsStdHr) || opsStdHr <= 0) return 0;
    if (!Number.isFinite(ops)) return 0;

    const result = (qStdHr / opsStdHr) * ops;
    return Number.isFinite(result) ? Number(result.toFixed(2)) : 0;
  }, [
    activeBatch,
    activeBatchStatus.reason,
    productInfo?.q_prod_std_hr,
    productInfo?.ciclo_x_hr_prod_std,
    rtOps,
  ]);

  // =========================
  // Cargar máquinas (solo una vez por id_c)
  // =========================
  useEffect(() => {
    const loadMachines = async () => {
      if (!id_c) return;
      try {
        const { data } = await apiClient.get(`/company/${id_c}/machines`);
        const list =
          data?.machines?.map((m) => ({
            cod_maq: m.cod_maq,
            name_m: m.name_m,
          })) || [];

        setAllMachines(list);
        if (!machineCode && list.length) setMachineCode(list[0].cod_maq);
      } catch (err) {
        console.error("Error loading machines:", err);
      }
    };

    loadMachines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id_c]);

  // =========================
  // Cargar lote activo (cada 10s)
  // =========================
  useEffect(() => {
    const loadActiveBatch = async () => {
      if (!id_c || !machineCode) return;

      setActiveBatchStatus({
        loading: true,
        reason: null,
        message: null,
        count: 0,
      });

      try {
        const res = await getActiveBatchTempByMachine({
          id_c,
          cod_maq: machineCode,
        });

        if (res?.ok && res?.batch) {
          setActiveBatch(res.batch);
          setActiveBatchStatus({
            loading: false,
            reason: res?.reason || "single_active_batch",
            message: null,
            count: res?.count || 1,
          });
          return;
        }

        setActiveBatch(res?.batch || null);
        setActiveBatchStatus({
          loading: false,
          reason: res?.reason || "unknown",
          message: res?.message || "No data",
          count: res?.count || 0,
        });
      } catch (e) {
        console.error("Error loading active batch:", e);
        setActiveBatch(null);
        setActiveBatchStatus({
          loading: false,
          reason: "error",
          message: "Error cargando lote activo",
          count: 0,
        });
      }
    };

    loadActiveBatch();
  }, [id_c, machineCode, refreshTick]);

  // =========================
  // Cargar producto (cada 10s)
  // =========================
  useEffect(() => {
    const loadProductInfo = async () => {
      if (!id_c || !machineCode || !activeBatch?.id_prod) {
        setProductInfo(null);
        setProductStatus({ loading: false, reason: "no_batch", message: null });
        return;
      }

      if (activeBatchStatus.reason === "multiple_active_batches") {
        setProductInfo(null);
        setProductStatus({
          loading: false,
          reason: "multiple_batches",
          message: "Cerrar lotes anteriores",
        });
        return;
      }

      setProductStatus({ loading: true, reason: null, message: null });

      try {
        const res = await getProdByMachineAndRef({
          id_c,
          cod_maq: machineCode,
          id_prod: activeBatch.id_prod,
        });

        if (res?.ok && res?.product) {
          setProductInfo(res.product);
          setProductStatus({ loading: false, reason: "ok", message: null });
          return;
        }

        setProductInfo(null);
        setProductStatus({
          loading: false,
          reason: res?.reason || "not_found",
          message: res?.message || "No product info",
        });
      } catch (e) {
        console.error("Error loading product info:", e);
        setProductInfo(null);
        setProductStatus({
          loading: false,
          reason: "error",
          message: "Error cargando producto",
        });
      }
    };

    loadProductInfo();
  }, [id_c, machineCode, activeBatch?.id_prod, activeBatchStatus.reason, refreshTick]);

  // =========================
  // Operaciones RT TOTAL (cada 10s)
  // =========================
  useEffect(() => {
    const loadOps = async () => {
      if (
        !id_c ||
        !machineCode ||
        !activeBatch?.date_begin ||
        !activeBatch?.time_begin ||
        activeBatchStatus.reason === "multiple_active_batches"
      ) {
        setRtOps(0);
        return;
      }

      const start_date = toYMD(activeBatch.date_begin);
      const start_time = String(activeBatch.time_begin).slice(0, 8);

      const end_date = fmtDateYMD(new Date());
      const end_time = fmtTimeHMS(new Date());

      const res = await getRtOperationsCount({
        id_c,
        cod_maq: machineCode,
        start_date,
        start_time,
        end_date,
        end_time,
      });

      setRtOps(Number(res?.total_act || 0));
    };

    loadOps();
  }, [
    id_c,
    machineCode,
    activeBatch?.date_begin,
    activeBatch?.time_begin,
    activeBatchStatus.reason,
    refreshTick,
  ]);

  // =========================
  // ✅ Operaciones por hora (para barras)
  // Incluye la hora actual "en progreso" (cierra en NOW)
  // =========================
  useEffect(() => {
    const floorToHour = (d) => {
      const x = new Date(d);
      x.setMinutes(0, 0, 0);
      return x;
    };

    const addHours = (d, h) => {
      const x = new Date(d);
      x.setHours(x.getHours() + h);
      return x;
    };

    const loadOpsByHour = async () => {
      if (
        !id_c ||
        !machineCode ||
        !activeBatch?.date_begin ||
        !activeBatch?.time_begin ||
        activeBatchStatus.reason === "multiple_active_batches" ||
        activeBatchStatus.reason === "no_active_batch"
      ) {
        setRtOpsByHour([]);
        return;
      }

      const startDateYMD = toYMD(activeBatch.date_begin);
      const startTime = String(activeBatch.time_begin || "").slice(0, 8);
      if (!startDateYMD || !startTime) {
        setRtOpsByHour([]);
        return;
      }

      const end = new Date();

      let batchStart = new Date(`${startDateYMD}T${startTime}`);
      if (end.getTime() - batchStart.getTime() < 0) {
        const tmp = new Date(batchStart);
        tmp.setDate(tmp.getDate() - 1);
        batchStart = tmp;
      }

      const maxBuckets = 12; // 👈 cuántas horas mostrar
      const endHour = floorToHour(end);
      let startHour = addHours(endHour, -(maxBuckets - 1));

      const batchStartHour = floorToHour(batchStart);
      if (startHour.getTime() < batchStartHour.getTime()) startHour = batchStartHour;

      const intervals = [];
      for (let h = new Date(startHour); h.getTime() <= endHour.getTime(); h = addHours(h, 1)) {
        const next = addHours(h, 1);

        // recorta primera hora si el lote inició a mitad de hora
        const realStart = h.getTime() < batchStart.getTime() ? new Date(batchStart) : new Date(h);

        // ✅ clave: para la hora actual, cerrar en NOW; para horas pasadas, cerrar en next
        const isCurrentHour = h.getTime() === endHour.getTime();
        const realEnd = isCurrentHour ? new Date(end) : new Date(next);

        if (realEnd.getTime() > realStart.getTime()) {
          intervals.push({ s: realStart, e: realEnd, label: fmtTimeHH00(h) });
        }
      }

      try {
        const settled = await Promise.allSettled(
          intervals.map(({ s, e }) =>
            getRtOperationsCount({
              id_c,
              cod_maq: machineCode,
              start_date: fmtDateYMD(s),
              start_time: fmtTimeHMS(s),
              end_date: fmtDateYMD(e),
              end_time: fmtTimeHMS(e),
            })
          )
        );

        const results = intervals.map((it, idx) => {
          const r = settled[idx];
          const total = r.status === "fulfilled" ? Number(r.value?.total_act || 0) : 0;
          return {
            name: it.label,
            value: Number.isFinite(total) ? total : 0,
          };
        });

        setRtOpsByHour(results);
      } catch (err) {
        console.error("Error loading ops by hour:", err);
        setRtOpsByHour((prev) => prev); // no borra la gráfica
      }
    };

    loadOpsByHour();
  }, [
    id_c,
    machineCode,
    activeBatch?.date_begin,
    activeBatch?.time_begin,
    activeBatchStatus.reason,
    refreshTick,
  ]);

  // =========================
  // KPI dinámicos
  // =========================
  const kpi = {
    loteActivo: (() => {
      if (activeBatchStatus.loading) return "—";
      if (activeBatchStatus.reason === "no_active_batch") return "—";
      if (activeBatchStatus.reason === "multiple_active_batches") return "⚠️";
      return activeBatch?.id_batch ?? "—";
    })(),

    referencia: (() => {
      if (activeBatchStatus.loading) return "Cargando...";
      if (activeBatchStatus.reason === "no_active_batch") return "Sin Lote Iniciado";
      if (activeBatchStatus.reason === "multiple_active_batches") return "⚠️ Lotes abiertos";
      return activeBatch?.id_prod ?? "—";
    })(),

    descripcion: (() => {
      if (activeBatchStatus.reason === "no_active_batch") return "—";
      if (activeBatchStatus.reason === "multiple_active_batches") return "Cerrar lotes anteriores";
      if (productStatus.loading) return "Cargando...";
      return productInfo?.name_prod ?? "Producto no encontrado";
    })(),

    horaInicio: (() => {
      if (activeBatchStatus.loading) return "—";
      if (!activeBatch || activeBatchStatus.reason === "no_active_batch") return "—";
      return String(activeBatch?.time_begin || "—").slice(0, 8);
    })(),

    qProdStdHr: (() => {
      if (activeBatchStatus.reason === "no_active_batch") return "—";
      if (activeBatchStatus.reason === "multiple_active_batches") return "—";
      if (productStatus.loading) return "—";
      const v = productInfo?.q_prod_std_hr;
      return v === 0 || v ? String(v) : "—";
    })(),

    operaciones: (() => {
      if (activeBatchStatus.reason === "no_active_batch") return "—";
      if (activeBatchStatus.reason === "multiple_active_batches") return "—";
      return activeBatch ? rtOps : "—";
    })(),

    horasUtil: (() => {
      if (activeBatchStatus.reason === "no_active_batch") return "—";
      if (activeBatchStatus.reason === "multiple_active_batches") return "—";
      if (!activeBatch) return "—";
      return hoursUsed === 0 ? "0.00" : hoursUsed.toFixed(2);
    })(),

    prodEstim: (() => {
      if (estimatedProducts === null) return "—";
      return Number(estimatedProducts).toFixed(2);
    })(),

    opsStdHr: (() => {
      if (activeBatchStatus.reason === "no_active_batch") return "—";
      if (activeBatchStatus.reason === "multiple_active_batches") return "—";
      if (productStatus.loading) return "—";
      const v = productInfo?.ciclo_x_hr_prod_std;
      return v === 0 || v ? String(v) : "—";
    })(),

    opsRealHr: (() => {
      if (activeBatchStatus.reason === "no_active_batch") return "—";
      if (activeBatchStatus.reason === "multiple_active_batches") return "—";
      if (!activeBatch) return "—";
      return realOpsPerHour === 0 ? "0.00" : realOpsPerHour.toFixed(2);
    })(),
  };

  // =========================
  // Números comparables (para color y eficiencia)
  // =========================
  const opsStdHrNum = useMemo(() => {
    const v = Number(productInfo?.ciclo_x_hr_prod_std);
    return Number.isFinite(v) ? v : null;
  }, [productInfo?.ciclo_x_hr_prod_std]);

  const opsRealHrNum = useMemo(() => {
    const v = Number(realOpsPerHour);
    return Number.isFinite(v) ? v : null;
  }, [realOpsPerHour]);

  // =========================
  // Color dinámico para "OPERACIONES REALES / HORA"
  // =========================
  const opsRealVsStdCardClass = useMemo(() => {
    if (
      !activeBatch ||
      activeBatchStatus.reason === "no_active_batch" ||
      activeBatchStatus.reason === "multiple_active_batches"
    ) {
      return "rt-card--dark";
    }
    if (opsStdHrNum === null) return "rt-card--dark";
    if (opsRealHrNum !== null && opsRealHrNum >= opsStdHrNum) return "rt-card--green";
    return "rt-card--red";
  }, [activeBatch, activeBatchStatus.reason, opsStdHrNum, opsRealHrNum]);

  // =========================
  // ✅ EFICIENCIA (%) para la gráfica izquierda
  // =========================
  const efficiencyPct = useMemo(() => {
    if (
      !activeBatch ||
      activeBatchStatus.reason === "no_active_batch" ||
      activeBatchStatus.reason === "multiple_active_batches"
    ) {
      return 0;
    }
    if (opsStdHrNum === null || opsStdHrNum <= 0) return 0;

    const v = (Number(opsRealHrNum || 0) / Number(opsStdHrNum)) * 100;
    return Number.isFinite(v) ? Number(v.toFixed(2)) : 0;
  }, [activeBatch, activeBatchStatus.reason, opsRealHrNum, opsStdHrNum]);

  return (
    <div className="screen1-rt">
      {/* ================= HEADER ================= */}
      <header className="rt-header">
        <div className="rt-header__left">
          <img className="rt-logo-client" src={company?.logo_c} alt="Company Logo" />
        </div>

        <div className="rt-header__date">{ddmmyyyy}</div>
        <div className="rt-header__time">{hhmmss}</div>

        <div className="rt-header__picker">
          <select
            className="rt-machine-select"
            value={machineCode}
            onChange={(e) => setMachineCode(e.target.value)}
          >
            {allMachines.map((m) => (
              <option key={m.cod_maq} value={m.cod_maq}>
                {m.cod_maq}
              </option>
            ))}
            {!allMachines.length && <option value="">Máquina</option>}
          </select>
        </div>

        <div className="rt-header__right" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "none", border: "1px solid rgba(255,255,255,0.2)",
              color: "rgba(255,255,255,0.45)", borderRadius: 6,
              padding: "4px 10px", fontSize: 12, cursor: "pointer",
              transition: "opacity 0.2s, color 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.45)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; }}
          >
            {t("screen_back")}
          </button>
          <img
            className="rt-logo-tt"
            src="https://storage.googleapis.com/tt202501/images/techtalamo_logo2.jpg"
            alt="Techtalamo"
          />
        </div>
      </header>

      {/* ✅ ALERTA si hay múltiples lotes abiertos */}
      {activeBatchStatus.reason === "multiple_active_batches" && (
        <div
          style={{
            marginTop: 10,
            padding: "10px 12px",
            borderRadius: 10,
            background: "#fff3cd",
            border: "1px solid #ffe69c",
          }}
        >
          <b>⚠️ Atención:</b> Hay <b>{activeBatchStatus.count}</b> lotes abiertos para la máquina{" "}
          <b>{machineCode}</b>. Debes tener <b>un solo lote</b> abierto en esa máquina. Por favor,
          cierra los lotes anteriores.
        </div>
      )}

      {/* ================= GRID 6x2 CARDS ================= */}
      <section className="rt-grid">
        {/* fila 1 */}
        <div className="rt-card rt-card--blue">
          <div className="rt-kpi-value">{kpi.loteActivo}</div>
          <div className="rt-kpi-label">LOTE ACTIVO</div>
        </div>

        <div className="rt-card rt-card--dark">
          <div className="rt-kpi-value">{kpi.referencia}</div>
          <div className="rt-kpi-label">REFERENCIA</div>
        </div>

        <div className="rt-card rt-card--dark rt-card--desc">
          <div className="rt-kpi-value rt-kpi-value--desc">{kpi.descripcion}</div>
          <div className="rt-kpi-label">DESCRIPCIÓN</div>
        </div>

        <div className="rt-card rt-card--dark">
          <div className="rt-kpi-value">{kpi.horaInicio}</div>
          <div className="rt-kpi-label">HORA INICIO</div>
        </div>

        {/* fila 2 */}
        <div className="rt-card rt-card--pink">
          <div className="rt-kpi-value">{kpi.horasUtil}</div>
          <div className="rt-kpi-label">HORAS UTILIZADAS</div>
        </div>

        <div className="rt-card rt-card--dark">
          <div className="rt-kpi-value">{kpi.operaciones}</div>
          <div className="rt-kpi-label">OPERACIONES</div>
        </div>

        <div className="rt-card rt-card--dark">
          <div className="rt-kpi-value">{kpi.qProdStdHr}</div>
          <div className="rt-kpi-label">Q PRODUCTOS ESTANDAR / HORA</div>
        </div>

        <div className="rt-card rt-card--pink">
          <div className="rt-kpi-value">{kpi.prodEstim}</div>
          <div className="rt-kpi-label">Q PRODUCTOS ESTIMADOS</div>
        </div>

        <div className="rt-card rt-card--dark">
          <div className="rt-kpi-value">{kpi.opsStdHr}</div>
          <div className="rt-kpi-label">OPERACIONES ESTANDAR / HORA</div>
        </div>

        <div className={`rt-card ${opsRealVsStdCardClass}`}>
          <div className="rt-kpi-value">{kpi.opsRealHr}</div>
          <div className="rt-kpi-label">OPERACIONES REALES / HORA</div>
        </div>
      </section>

      {/* ================= CHARTS BOTTOM 50/50 ================= */}
      <section className="rt-charts">
        <div className="rt-panel">
          <DoughnutChart2 value={efficiencyPct} label={"EFICIENCIA"} size="large" />
        </div>

        <div className="rt-panel">
<RTBarChartWithStdLine
  data={rtOpsByHour}
  title="OPERACIONES / HORA"
  stdValue={opsStdHrNum}   // <- OPERACIONES ESTANDAR / HORA
/>
        </div>
      </section>
    </div>
  );
};

export default Screen1;